import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '../../api/client'
import { useAction, useList } from '../../api/queries'
import type { Equipment } from '../../api/types'
import { Field, Modal } from '../../components/ui'
import { areaLabel } from '../../lib/areas'
import { InlineCreateForm } from './InlineCreateForm'
import { buildCreatePayload, emptyFormState, validateForm, type FormState } from './equipmentForm'
import { EquipmentSpecFields, FORM_ERROR_KEYS } from './EquipmentSpecFields'

const KNOWN_ERROR_KEYS = new Set(['area', 'code', 'is_route_critical', 'route_critical_source', ...FORM_ERROR_KEYS])

type Values = Record<string, string>

export function NewEquipmentModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const [v, setV] = useState<Values>({ plant: '', area: '' })
  const [spec, setSpec] = useState<FormState>(emptyFormState)
  const [routeCritical, setRouteCritical] = useState(false)
  const [local, setLocal] = useState<Record<string, string>>({})
  const [error, setError] = useState<ApiError | null>(null)
  const [creating, setCreating] = useState<'plant' | 'area' | null>(null)
  const plants = useList<{ id: number; code: string; name: string }>('/api/plants/', { page_size: 200 })
  const areas = useList<{ id: number; code: string; name: string }>('/api/areas/', { plant: v.plant, page_size: 200 }, { enabled: v.plant !== '' })
  const create = useAction<Record<string, unknown>, Equipment>('post', '/api/equipment/', ['/api/equipment', '/api/assets/tree/'])
  const set = (k: string) => (e: { target: { value: string } }) => {
    if (k === 'plant') setCreating(null)
    setV({ ...v, [k]: e.target.value, ...(k === 'plant' ? { area: '' } : {}) })
  }

  async function submit(e: FormEvent) {
    e.preventDefault(); setError(null)
    const problems: Record<string, string> = { ...validateForm(spec) }
    if (!v.area) problems.area = 'Seleccionar un área.'
    if (routeCritical && !v.route_critical_source?.trim()) problems.route_critical_source = 'Indicar la fuente de la ruta crítica.'
    setLocal(problems)
    if (Object.keys(problems).length) return
    const body: Record<string, unknown> = { area: Number(v.area), ...buildCreatePayload(spec) }
    const code = v.code?.trim()
    if (code) body.code = code
    if (routeCritical) { body.is_route_critical = true; body.route_critical_source = v.route_critical_source.trim() }
    try {
      const created = await create.mutateAsync(body)
      onClose()
      navigate(`/assets/${created.id}`)
    } catch (err) { setError(err as ApiError) }
  }

  const fe = { ...(error?.fieldErrors ?? {}), ...local }
  const extra = Object.entries(error?.fieldErrors ?? {}).filter(([k]) => !KNOWN_ERROR_KEYS.has(k)).map(([, m]) => m)
  const generic = error && (!Object.keys(error.fieldErrors).length || extra.length) ? (extra.length ? extra.join(' ') : error.message) : null
  const noAreas = v.plant !== '' && areas.isSuccess && areas.data.results.length === 0

  return (
    <Modal title="Nuevo equipo" onClose={onClose} wide>
      <form className="form-stack" onSubmit={submit} noValidate>
        <Field label="Planta" htmlFor="eq-plant"><select id="eq-plant" value={v.plant} onChange={set('plant')}>
          <option value="">Seleccionar planta</option>
          {plants.data?.results.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select></Field>
        <div><button type="button" className="btn btn--ghost" disabled={creating === 'plant'} onClick={() => setCreating('plant')}>+ Nueva planta</button></div>
        {creating === 'plant' ? (
          <InlineCreateForm title="Nueva planta" endpoint="/api/plants/" invalidate={['/api/plants/', '/api/assets/tree/']} idPrefix="new-plant"
            labels={{ code: 'Código de la planta', name: 'Nombre de la planta' }}
            onCancel={() => setCreating(null)}
            onCreated={(id) => { setV((cur) => ({ ...cur, plant: String(id), area: '' })); setCreating(null) }} />
        ) : null}
        <Field label="Área" htmlFor="eq-area" error={fe.area ?? (noAreas ? 'No hay áreas registradas en esta planta. Usar «+ Nueva área» para crear una antes de agregar equipos.' : undefined)}>
          <select id="eq-area" value={v.area} onChange={set('area')} disabled={v.plant === ''}>
            <option value="">{v.plant === '' ? 'Seleccionar primero una planta' : 'Seleccionar área'}</option>
            {areas.data?.results.map((a) => <option key={a.id} value={a.id}>{areaLabel(a.code, a.name)}</option>)}
          </select>
        </Field>
        <div><button type="button" className="btn btn--ghost" disabled={v.plant === '' || creating === 'area'} onClick={() => setCreating('area')}>+ Nueva área</button></div>
        {creating === 'area' ? (
          <InlineCreateForm title="Nueva área" endpoint="/api/areas/" invalidate={['/api/areas/', '/api/assets/tree/']} idPrefix="new-area"
            extra={{ plant: Number(v.plant) }}
            labels={{ code: 'Código del área', name: 'Nombre del área' }}
            onCancel={() => setCreating(null)}
            onCreated={(id) => { setV((cur) => ({ ...cur, area: String(id) })); setCreating(null) }} />
        ) : null}
        <Field label="Nombre" htmlFor="eq-name" error={fe.name}><input id="eq-name" value={spec.values.name} onChange={(e) => setSpec((s) => ({ ...s, values: { ...s.values, name: e.target.value } }))} /></Field>
        <Field label="Código" htmlFor="eq-code" error={fe.code} hint="Si se deja vacío se genera automáticamente. No se puede cambiar después."><input id="eq-code" value={v.code ?? ''} onChange={set('code')} maxLength={30} autoComplete="off" /></Field>
        <label className="check"><input type="checkbox" checked={routeCritical} onChange={(e) => setRouteCritical(e.target.checked)} /> Ruta crítica</label>
        {fe.is_route_critical ? <p role="alert" className="form-error">{fe.is_route_critical}</p> : null}
        {routeCritical ? <Field label="Fuente de la ruta crítica" htmlFor="eq-source" error={fe.route_critical_source}><input id="eq-source" value={v.route_critical_source ?? ''} onChange={set('route_critical_source')} /></Field> : null}
        <h3 className="spec__heading">Ficha técnica (opcional)</h3>
        <EquipmentSpecFields state={spec} setState={setSpec} errors={fe} idPrefix="eq" defaultOpen={false} />
        {generic ? <p role="alert" className="form-error">{generic}</p> : null}
        <div className="row">
          <button type="button" className="btn btn--ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn--primary" disabled={create.isPending}>Crear equipo</button>
        </div>
      </form>
    </Modal>
  )
}
